import ActBadges from '../../components/ui/ActBadges'
import { EntityLink as Link } from '../../navigation/navigation'
import { useListState } from '../../navigation/useListState'
import React, { useState, useEffect, useMemo } from 'react'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import Icon from '../../components/Icon'
import FilterBar from '../../components/FilterBar'
import { Input, Select, SearchableSelect, Button, Pagination } from '../../components/ui'
import ConfirmModal from '../../components/ConfirmModal'
import './Company.css'

function getCreateActObjectId(contract) {
  const objectId = String(contract?.object_id || '').trim()
  if (objectId) return objectId
  return `__no_object__:${String(contract?.object_name || '').trim() || 'no-name'}`
}

function createSearchText(...values) {
  return values
    .filter(Boolean)
    .join(' ')
    .trim()
    .toLowerCase()
}

function Acts() {
  const { filters, setFilters, pagination, setPagination, search, setSearch, sorting, setSorting } = useListState({
    filters: {
      contract_id: '',
      status: '',
      payment_type: '',
      issue_date_from: '',
      issue_date_to: ''
    },
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 1
    },
    search: '',
    sorting: {
      field: 'issue_date',
      order: 'desc'
    },
  })
  const navigate = useNavigate()
  const { user } = useAuth()
  const canCreate = user?.permissions?.['acts.create']
  const [acts, setActs] = useState([])
  const [contracts, setContracts] = useState([])
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedObjectId, setSelectedObjectId] = useState('')
  const [selectedContractId, setSelectedContractId] = useState('')
  const [loading, setLoading] = useState(true)

  const [totalAmount, setTotalAmount] = useState(0)

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const effectiveSearch = (search || '').trim().length >= 3 ? (search || '').trim() : ''

  useEffect(() => {
    fetchContracts()
  }, [])

  useEffect(() => {
    fetchActs()
  }, [filters, effectiveSearch, pagination.page, sorting])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchContracts = async () => {
    try {
      const response = await api.get('/company/contracts?limit=1000')
      if (response.data && response.data.contracts) {
        setContracts(response.data.contracts)
      }
    } catch (error) {
      console.error('Error fetching contracts:', error)
    }
  }

  const fetchActs = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.contract_id) params.append('contract_id', filters.contract_id)
      if (filters.status) params.append('status', filters.status)
      if (filters.payment_type) params.append('payment_type', filters.payment_type)
      if (filters.issue_date_from) params.append('issue_date_from', filters.issue_date_from)
      if (filters.issue_date_to) params.append('issue_date_to', filters.issue_date_to)
      if (effectiveSearch) params.append('search', effectiveSearch)
      params.append('page', pagination.page.toString())
      params.append('limit', pagination.limit.toString())
      params.append('sort_by', sorting.field)
      params.append('sort_order', sorting.order)

      const response = await api.get(`/company/acts?${params}`)
      
      if (response.data) {
        if (response.data.acts) {
          setActs(response.data.acts)
          setPagination(prev => ({
            ...prev,
            total: response.data.total || 0,
            pages: response.data.pages || 1
          }))
          setTotalAmount(response.data.total_amount || 0)
        } else {
          setActs([])
          setTotalAmount(0)
        }
      } else {
        setActs([])
        setTotalAmount(0)
      }
    } catch (error) {
      console.error('Error fetching acts:', error)
      console.error('Error details:', error.response?.data || error.message)
      setActs([])
      setTotalAmount(0)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleSearchChange = (value) => {
    const hadEffective = (search || '').trim().length >= 3
    const hasEffective = (value || '').trim().length >= 3
    setSearch(value)
    if (hadEffective !== hasEffective) setPagination(prev => ({ ...prev, page: 1 }))
  }

  const parseDate = (dateString) => {
    if (!dateString) return null
    const date = new Date(dateString)
    return isNaN(date.getTime()) ? null : date
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const handleSort = (field) => {
    setSorting(prev => {
      // Если кликнули по тому же полю, меняем порядок сортировки
      if (prev.field === field) {
        return {
          field: field,
          order: prev.order === 'asc' ? 'desc' : 'asc'
        }
      }
      // Если кликнули по другому полю, сортируем по возрастанию
      return {
        field: field,
        order: 'asc'
      }
    })
    // Сбрасываем страницу на первую при изменении сортировки
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const getSortIcon = (field) => {
    if (sorting.field !== field) {
      return <Icon name="chevronRight" size={14} style={{ opacity: 0.3 }} />
    }
    return sorting.order === 'asc' ? <Icon name="chevronUp" size={14} /> : <Icon name="chevronDown" size={14} />
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    // Пробуем разные форматы даты
    let date
    if (typeof dateString === 'string') {
      // Формат YYYY-MM-DD
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = new Date(dateString)
      } else {
        // Пробуем распарсить как есть
        date = new Date(dateString)
      }
    } else {
      date = new Date(dateString)
    }
    
    // Проверяем валидность даты
    if (isNaN(date.getTime())) {
      return '-'
    }
    
    return date.toLocaleDateString('ru-RU')
  }

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '-'
    
    // Преобразуем в число, если это строка
    let numAmount = amount
    if (typeof amount === 'string') {
      // Убираем пробелы и другие символы
      const cleaned = amount.trim().replace(/\s/g, '').replace(/[^\d.,-]/g, '')
      if (!cleaned) return '-'
      // Заменяем запятую на точку для десятичных чисел
      numAmount = parseFloat(cleaned.replace(',', '.'))
    }
    
    // Проверяем, что это валидное число
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return '-'
    }
    
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(numAmount)
  }

  const getMismatchTitle = (act) => {
    if (!act?.distribution_mismatch) return undefined
    const actAmount = act.amount == null ? '-' : formatAmount(act.amount)
    const afterTax = act.amount_after_tax == null ? null : formatAmount(act.amount_after_tax)
    const distSum = act.distribution_sum == null ? '0' : formatAmount(act.distribution_sum)
    const toNumber = (value) => {
      if (value === null || value === undefined || value === '') return null
      if (typeof value === 'number') return Number.isFinite(value) ? value : null
      const cleaned = String(value).trim().replace(/\s/g, '').replace(/[^\d.,-]/g, '')
      if (!cleaned) return null
      const num = parseFloat(cleaned.replace(',', '.'))
      return Number.isFinite(num) ? num : null
    }
    if (act.amount_after_tax == null) {
      return `Не заполнено «Сумма за вычетом налогов». Сумма акта: ${actAmount}. Откройте акт для пересчёта.`
    }
    if ((act.distribution_count || 0) === 0) {
      return `Нет распределения в «Акты по сотрудникам». Сумма акта за вычетом налогов: ${afterTax}`
    }
    const afterTaxNum = toNumber(act.amount_after_tax)
    const distSumNum = toNumber(act.distribution_sum) ?? 0
    const diff = afterTaxNum == null ? null : Math.abs(afterTaxNum - distSumNum)
    const diffPct =
      afterTaxNum && afterTaxNum > 0 && diff != null ? (diff / afterTaxNum) * 100 : null
    const diffLabel = diff != null ? formatAmount(diff) : '-'
    const pctLabel = diffPct != null ? `${diffPct.toFixed(1)}%` : '—'
    return `Сумма распределения (${distSum}) не совпадает с суммой за вычетом налогов (${afterTax}). Разница: ${diffLabel} (${pctLabel})`
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (filters.contract_id) count++
    if (filters.status) count++
    if (filters.payment_type) count++
    if (effectiveSearch) count++
    if (filters.issue_date_from) count++
    if (filters.issue_date_to) count++
    return count
  }

  const clearAllFilters = () => {
    setFilters({
      contract_id: '',
      status: '',
      payment_type: '',
      issue_date_from: '',
      issue_date_to: ''
    })
    setSearch('')
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleCreateAct = () => {
    if (contracts.length === 0) return
    if (contracts.length === 1) {
      navigate(`/company/contracts/${contracts[0].id}/acts/new`)
      return
    }
    setSelectedObjectId('')
    setSelectedContractId('')
    setCreateModalOpen(true)
  }

  const handleCreateActConfirm = () => {
    if (selectedContractId) {
      setCreateModalOpen(false)
      setSelectedObjectId('')
      setSelectedContractId('')
      navigate(`/company/contracts/${selectedContractId}/acts/new`)
    }
  }

  const objectOptionsForCreate = useMemo(() => {
    const byId = new Map()
    contracts.forEach((contract) => {
      const id = getCreateActObjectId(contract)
      if (byId.has(id)) return
      const name = String(contract.object_name || '').trim() || 'Без объекта'
      byId.set(id, {
        value: id,
        label: `${name}${contract.client_name ? ` · ${String(contract.client_name).trim()}` : ''}`,
        searchText: createSearchText(name, contract.client_name),
      })
    })
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, 'ru'))
  }, [contracts])

  const contractsForSelectedObject = selectedObjectId
    ? contracts.filter((contract) => getCreateActObjectId(contract) === selectedObjectId)
    : contracts
  const contractOptionsForCreate = contractsForSelectedObject.map((contract) => ({
    value: String(contract.id),
    label: contract.number || '-',
    description: [contract.object_name, contract.client_name].filter(Boolean).join(' · '),
    searchText: createSearchText(contract.number, contract.object_name, contract.client_name),
  }))
  const isSelectedContractVisible = contractsForSelectedObject.some((c) => String(c.id) === selectedContractId)

  const handleCreateObjectChange = (nextObjectId) => {
    setSelectedObjectId(nextObjectId)
    if (!nextObjectId) {
      setSelectedContractId('')
      return
    }
    const selectedContract = contracts.find((contract) => String(contract.id) === selectedContractId)
    if (selectedContract && getCreateActObjectId(selectedContract) !== nextObjectId) {
      setSelectedContractId('')
    }
  }

  const handleCreateContractChange = (nextContractId) => {
    setSelectedContractId(nextContractId)
    if (!nextContractId) return
    const contract = contracts.find((item) => String(item.id) === nextContractId)
    if (contract) {
      setSelectedObjectId(getCreateActObjectId(contract))
    }
  }

  usePageHeader({
    renderActions: () => canCreate ? (
      <Button
        variant="primary"
        onClick={handleCreateAct}
        disabled={contracts.length === 0}
        title={contracts.length === 0 ? 'Нет договоров для создания акта' : '+ Создать акт'}
      >
        + Создать акт
      </Button>
    ) : null
  }, [canCreate, contracts.length])

  if (loading) {
    return (
      <div className="company-page">
        <div className="loading-skeleton">
          <div className="skeleton-header"></div>
          <div className="skeleton-filters">
            <div className="skeleton-filter"></div>
            <div className="skeleton-filter"></div>
            <div className="skeleton-filter"></div>
          </div>
          <div className="skeleton-table">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-row"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="company-page">
      {createModalOpen && (
        <ConfirmModal
          title="Создание акта"
          message={
            <div className="create-act-modal-content">
              <p>Выберите объект и договор для создания акта:</p>
              <SearchableSelect
                id="create-act-object"
                label="Выберите объект"
                placeholder="Начните вводить объект или клиента"
                value={selectedObjectId}
                options={objectOptionsForCreate}
                onChange={handleCreateObjectChange}
              />
              <SearchableSelect
                id="create-act-contract"
                label="Выберите договор"
                placeholder="Начните вводить номер договора"
                value={selectedContractId}
                options={contractOptionsForCreate}
                emptyText={selectedObjectId ? 'У выбранного объекта нет договоров' : 'Договоры не найдены'}
                onChange={handleCreateContractChange}
              />
            </div>
          }
          confirmLabel="+ Создать акт"
          cancelLabel="Отмена"
          confirmVariant="primary"
          onConfirm={handleCreateActConfirm}
          onCancel={() => setCreateModalOpen(false)}
          confirmDisabled={!selectedContractId || !isSelectedContractVisible}
        />
      )}
      <div className="filters-section">
        <FilterBar
          expandable
          expanded={filtersExpanded}
          onToggle={() => setFiltersExpanded(!filtersExpanded)}
          filterCount={getActiveFiltersCount()}
          onClear={clearAllFilters}
          clearLabel={isMobile ? 'Сбросить все' : 'Очистить фильтры'}
          isMobile={isMobile}
        >
          <div className="filter-group">
            <label>Договор</label>
            <Select
              value={filters.contract_id}
              onChange={(e) => handleFilterChange('contract_id', e.target.value)}
              aria-label="Договор"
            >
              <option value="">Все договоры</option>
              {contracts.map((contract) => (
                <option key={contract.id} value={contract.id}>
                  {contract.number || '-'}
                </option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label>Статус</label>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              aria-label="Статус акта"
            >
              <option value="">Все статусы</option>
              <option value="не подписан">не подписан</option>
              <option value="на подписании">на подписании</option>
              <option value="подписан">подписан</option>
              <option value="не оплачен">не оплачен</option>
              <option value="оплачен">оплачен</option>
              <option value="none">Без статуса (средства артели)</option>
            </Select>
          </div>
          <div className="filter-group">
            <label>Тип оплаты</label>
            <Select value={filters.payment_type || ''} aria-label="Тип оплаты акта"
              onChange={(e) => handleFilterChange('payment_type', e.target.value)}>
              <option value="">Все типы</option>
              <option value="bank_transfer">Безналичный</option>
              <option value="cash">Наличные</option>
              <option value="artel">Средства артели</option>
            </Select>
          </div>
          <div className="filter-group filter-group-search">
            <label>Поиск</label>
            <Input
              type="text"
              placeholder="Номер акта или договора"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Поиск по номеру акта или договора"
            />
          </div>
          <div className="filters-dates-row">
            <div className="filter-group">
              <label>Дата с</label>
              <Input
                type="date"
                value={filters.issue_date_from || ''}
                onChange={(e) => handleFilterChange('issue_date_from', e.target.value)}
                className="date-picker-input"
                aria-label="Дата с"
              />
            </div>
            <div className="filter-group">
              <label>Дата по</label>
              <Input
                type="date"
                value={filters.issue_date_to || ''}
                onChange={(e) => handleFilterChange('issue_date_to', e.target.value)}
                className="date-picker-input"
                aria-label="Дата по"
              />
            </div>
          </div>
        </FilterBar>
      </div>

      <div className="table-section">
        {acts.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon">
              <Icon name="file" size={48} />
            </div>
            <div className="no-data-text">Акты не найдены</div>
            {getActiveFiltersCount() > 0 && (
              <Button variant="ghost" className="no-data-button" onClick={clearAllFilters}>
                Сбросить фильтры
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="table-summary-compact">
              Найдено {pagination.total} актов на общую сумму {formatAmount(totalAmount)}
            </div>
            {isMobile ? (
              <>
              <div className="acts-cards">
                {acts.map((act) => (
                  <div
                    key={act.id}
                    className={`act-card ui-card ui-card-hover${act.distribution_mismatch ? ' act-card-mismatch' : ''}`}
                    title={getMismatchTitle(act)}
                    onClick={() => navigate(`/act/${act.id}`)}
                  >
                    <div className="act-card-header">
                      <div className="act-card-title">
                        <Link className="act-number" to={`/act/${act.id}`}>Акт №{act.act_number || act.number || '-'}</Link>
                        <ActBadges act={act} />
                      </div>
                    </div>
                    <div className="act-card-body">
                      <div className="act-card-row">
                        <span className="act-card-label">Договор:</span>
                        <Link className="act-card-value clickable-link" to={act.contract_id ? `/contract/${act.contract_id}` : undefined}
                        >
                          {act.contract_number || '-'}
                        </Link>
                      </div>
                      <div className="act-card-row">
                        <span className="act-card-label">Клиент:</span>
                        <Link className="act-card-value clickable-link" to={act.client_id ? `/client/${act.client_id}` : undefined}>
                          {act.client_name || '-'}
                        </Link>
                      </div>
                      <div className="act-card-row">
                        <span className="act-card-label">Стадия:</span>
                        <span className="act-card-value">{act.stage_name || '-'}</span>
                      </div>
                      <div className="act-card-row act-card-row-highlight">
                        <span className="act-card-label">Сумма:</span>
                        <span className="act-card-value act-card-amount">{formatAmount(act.amount)}</span>
                      </div>
                      <div className="act-card-row">
                        <span className="act-card-label">Дата:</span>
                        <span className="act-card-value">{formatDate(act.issue_date)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                {pagination.total > 0 && (
                  <div className="company-pagination-bar-wrap">
                    <Pagination
                      page={pagination.page}
                      pages={pagination.pages}
                      total={pagination.total}
                      pageSize={pagination.limit}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="company-table-frame">
              <div className="table-responsive">
                <table className="ds-data-table acts-data-table">
                  <thead>
                    <tr>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('number')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        № {getSortIcon('number')}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('client')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Клиент {getSortIcon('client')}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('contract')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Договор {getSortIcon('contract')}
                      </th>
                      <th 
                        className="sortable" 
                        onClick={() => handleSort('stage')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Стадия {getSortIcon('stage')}
                      </th>
                      <th 
                        className="text-right sortable" 
                        onClick={() => handleSort('amount')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Сумма {getSortIcon('amount')}
                      </th>
                      <th 
                        className="text-right sortable" 
                        onClick={() => handleSort('date')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Дата {getSortIcon('date')}
                      </th>
                      <th>Тип оплаты / статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acts.map((act) => (
                      <tr
                        key={act.id}
                        className={`clickable-row${act.distribution_mismatch ? ' act-row-mismatch' : ''}`}
                        title={getMismatchTitle(act)}
                        onClick={() => navigate(`/act/${act.id}`)}
                      >
                        <td>
                          <Link to={`/act/${act.id}`} className="list-primary-name">{act.act_number || act.number || '-'}</Link>
                        </td>
                        <td
                          onClick={(e) => {
                            e.stopPropagation()
                            if (act.client_id) {
                              navigate(`/client/${act.client_id}`)
                            }
                          }}
                        >
                          {act.client_id ? (
                            <Link className="ui-table-entity-link" to={act.client_id ? `/client/${act.client_id}` : undefined}>{act.client_name || '-'}</Link>
                          ) : (
                            act.client_name || '-'
                          )}
                        </td>
                        <td
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/contract/${act.contract_id}`)
                          }}
                        >
                          <Link className="ui-table-entity-link" to={act.contract_id ? `/contract/${act.contract_id}` : undefined}>{act.contract_number || '-'}</Link>
                        </td>
                        <td>{act.stage_name || '-'}</td>
                        <td className="text-right mono">{formatAmount(act.amount)}</td>
                        <td className="text-right mono">{formatDate(act.issue_date)}</td>
                        <td>
                          <ActBadges act={act} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                {pagination.total > 0 && (
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Acts
